import React, { useEffect, useState } from "react";

import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from "@mui/material";

import Grid from "@mui/material/Grid2";

import {
    AddOutlined as AddOutlinedIcon,
    Close as CloseIcon,
    DeleteOutline as DeleteOutlineIcon,
    CloudUploadOutlined as CloudUploadOutlinedIcon,
    AttachFileOutlined as AttachFileOutlinedIcon
} from "@mui/icons-material";

import AxiosInstance from "../../services/AxiosInstance";

import { ThemeProvider } from "../v2/home/theme/ThemeProvider";

import {
    CONFIGURATION_CATEGORY_URL,
    CHANGE_TYPE_URL,
    URGENCIES_URL,
    IMPACTS_URL,
    AFFECTED_SERVICES_URL,
    ASSET_ITEM_URL,
    CONFIGURATION_ITEM_URL,
    DEPARTMETNS_URL,
    MAINTENANCE_WINDOW_URL,
    USERS_URL,
    CHANGE_REQUEST_URL
} from "../../Constants/Constants";

import { useAlert } from "../alerts/AlertContext";


const CreateChangeDialog = ({
    open,
    onClose,
    onSaved,
    changeId = null
}) => {

    const showAlert = useAlert();

    const [saving, setSaving] =
        useState(false);

    const [loadingMasters, setLoadingMasters] =
        useState(false);

    const [loadingChange, setLoadingChange] =
        useState(false);

    const [confirmOpen, setConfirmOpen] =
        useState(false);


    /*
     * ============================================================
     * FORM DATA
     * ============================================================
     */

    const initialForm = {
        title: "",
        description: "",

        changeTypeId: "",
        categoryId: "",
        priorityId: "",
        riskId: "",

        affectedServiceId: "",
        affectedAssetId: "",
        configurationItemId: "",
        departmentId: "",

        businessImpact: "",

        plannedStartDate: "",
        plannedEndDate: "",

        estimatedDurationValue: "",
        estimatedDurationUnit: "HOURS",

        expectedDowntimeValue: "",
        expectedDowntimeUnit: "MINUTES",

        maintenanceWindowId: "",

        implementationPlan: "",
        testingPlan: "",
        rollbackPlan: "",

        approverIds: []
    };


    const [formData, setFormData] =
        useState(initialForm);


    /*
     * ============================================================
     * MASTER DATA
     * ============================================================
     */

    const [categories, setCategories] =
        useState([]);

    const [changeTypes, setChangeTypes] =
        useState([]);

    const [priorities, setPriorities] =
        useState([]);

    const [risks, setRisks] =
        useState([]);

    const [affectedServices, setAffectedServices] =
        useState([]);

    const [assets, setAssets] =
        useState([]);

    const [configurationItems, setConfigurationItems] =
        useState([]);

    const [departments, setDepartments] =
        useState([]);

    const [maintenanceWindows, setMaintenanceWindows] =
        useState([]);

    const [users, setUsers] =
        useState([]);


    /*
     * ============================================================
     * ATTACHMENTS
     * ============================================================
     */

    const [attachments, setAttachments] =
        useState([]);


    /*
     * ============================================================
     * LOAD MASTER DATA
     * ============================================================
     */

    const loadMasters = async () => {

        setLoadingMasters(true);

        try {

            const [
                categoryResponse,
                changeTypeResponse,
                priorityResponse,
                riskResponse,
                affectedServiceResponse,
                assetResponse,
                configurationItemResponse,
                departmentResponse,
                maintenanceWindowResponse
            ] = await Promise.all([

                AxiosInstance.get(
                    CONFIGURATION_CATEGORY_URL
                ),

                AxiosInstance.get(
                    CHANGE_TYPE_URL
                ),

                AxiosInstance.get(
                    URGENCIES_URL
                ),

                AxiosInstance.get(
                    IMPACTS_URL
                ),

                AxiosInstance.get(
                    AFFECTED_SERVICES_URL
                ),

                AxiosInstance.get(
                    ASSET_ITEM_URL
                ),

                AxiosInstance.get(
                    CONFIGURATION_ITEM_URL
                ),

                AxiosInstance.get(
                    DEPARTMETNS_URL
                ),

                AxiosInstance.get(
                    MAINTENANCE_WINDOW_URL
                )
            ]);


            setCategories(
                categoryResponse.data?.data ||
                categoryResponse.data ||
                []
            );


            setChangeTypes(
                changeTypeResponse.data?.data ||
                changeTypeResponse.data ||
                []
            );


            setPriorities(
                priorityResponse.data?.data ||
                priorityResponse.data ||
                []
            );


            setRisks(
                riskResponse.data?.data ||
                riskResponse.data ||
                []
            );


            setAffectedServices(
                affectedServiceResponse.data?.data ||
                affectedServiceResponse.data ||
                []
            );


            setAssets(
                assetResponse.data?.data ||
                assetResponse.data ||
                []
            );


            setConfigurationItems(
                configurationItemResponse.data?.data ||
                configurationItemResponse.data ||
                []
            );


            setDepartments(
                departmentResponse.data?.data ||
                departmentResponse.data ||
                []
            );


            setMaintenanceWindows(
                maintenanceWindowResponse.data?.data ||
                maintenanceWindowResponse.data ||
                []
            );


        } catch (error) {

            console.error(
                "Unable to load change masters",
                error
            );

            showAlert(
                "Unable to load change master data",
                "error"
            );

        } finally {

            setLoadingMasters(false);
        }
    };

    const toUTC = (value) => {
        if (!value) {
            return null;
        }

        return new Date(value).toISOString();
    };

    /*
     * ============================================================
     * SEARCH USERS
     * ============================================================
     */

    const searchUsers = async (searchValue) => {

        if (
            !searchValue ||
            searchValue.trim().length < 2
        ) {
            return;
        }

        try {

            const response =
                await AxiosInstance.get(
                    `${USERS_URL}/search`,
                    {
                        params: {
                            search:
                                searchValue.trim()
                        }
                    }
                );

            setUsers(
                response.data?.data ||
                response.data ||
                []
            );

        } catch (error) {

            console.error(
                "Unable to search users",
                error
            );
        }
    };


    /*
     * ============================================================
     * LOAD EXISTING CHANGE
     * ============================================================
     */

    const loadChange = async () => {

        if (!changeId) {
            return;
        }

        setLoadingChange(true);

        try {

            const response =
                await AxiosInstance.get(
                    `${CHANGE_REQUEST_URL}/${changeId}`
                );

            const change =
                response.data?.data ||
                response.data;


            setFormData({
                title:
                    change.title || "",

                description:
                    change.description || "",

                changeTypeId:
                    change.changeTypeId != null
                        ? String(change.changeTypeId)
                        : "",

                categoryId:
                    change.categoryId != null
                        ? String(change.categoryId)
                        : "",

                priorityId:
                    change.priorityId != null
                        ? String(change.priorityId)
                        : "",

                riskId:
                    change.riskId != null
                        ? String(change.riskId)
                        : "",

                affectedServiceId:
                    change.affectedServiceId != null
                        ? String(change.affectedServiceId)
                        : "",

                affectedAssetId:
                    change.affectedAssetId != null
                        ? String(change.affectedAssetId)
                        : "",

                configurationItemId:
                    change.configurationItemId != null
                        ? String(change.configurationItemId)
                        : "",

                departmentId:
                    change.departmentId != null
                        ? String(change.departmentId)
                        : "",

                businessImpact:
                    change.businessImpact || "",

                plannedStartDate:
                    formatDateTimeLocal(change.plannedStartDate) || "",

                plannedEndDate:
                    formatDateTimeLocal(change.plannedEndDate) || "",

                estimatedDurationValue:
                    change.estimatedDurationValue ?? "",

                estimatedDurationUnit:
                    change.estimatedDurationUnit ||
                    "HOURS",

                expectedDowntimeValue:
                    change.expectedDowntimeValue ?? "",

                expectedDowntimeUnit:
                    change.expectedDowntimeUnit ||
                    "MINUTES",

                maintenanceWindowId:
                    change.maintenanceWindowId != null
                        ? String(change.maintenanceWindowId)
                        : "",

                implementationPlan:
                    change.implementationPlan || "",

                testingPlan:
                    change.testingPlan || "",

                rollbackPlan:
                    change.rollbackPlan || "",

                approverIds:
                    Array.isArray(change.approverIds)
                        ? change.approverIds.map(String)
                        : []
            });


            if (
                Array.isArray(
                    change.approvers
                )
            ) {
                setUsers(
                    change.approvers
                );
            }


            if (
                Array.isArray(
                    change.attachments
                )
            ) {
                setAttachments(
                    change.attachments
                );
            }


        } catch (error) {

            console.error(
                "Unable to load change",
                error
            );

            showAlert(
                "Unable to load change request",
                "error"
            );

        } finally {

            setLoadingChange(false);
        }
    };


    /*
     * ============================================================
     * OPEN
     * ============================================================
     */

    useEffect(() => {

        if (!open) {
            return;
        }

        loadMasters();

        if (changeId) {
            loadChange();
        } else {

            setFormData(
                initialForm
            );

            setAttachments([]);

            setUsers([]);
        }

    }, [open, changeId]);


    /*
     * ============================================================
     * NORMAL FIELD CHANGE
     * ============================================================
     */

    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };


    /*
     * ============================================================
     * AUTOCOMPLETE HELPER
     * ============================================================
     */

    const findOption = (
        options,
        id
    ) => {

        if (
            id === null ||
            id === undefined ||
            id === ""
        ) {
            return null;
        }

        return options.find(
            item =>
                String(item.id) ===
                String(id)
        ) || null;
    };


    /*
     * ============================================================
     * VALIDATION
     * ============================================================
     */

    const validate = () => {

        if (
            !formData.title ||
            !formData.title.trim()
        ) {

            showAlert(
                "Change title is required",
                "error"
            );

            return false;
        }


        if (!formData.changeTypeId) {

            showAlert(
                "Change type is required",
                "error"
            );

            return false;
        }


        if (!formData.categoryId) {

            showAlert(
                "Category is required",
                "error"
            );

            return false;
        }


        if (!formData.priorityId) {

            showAlert(
                "Priority is required",
                "error"
            );

            return false;
        }


        if (!formData.riskId) {

            showAlert(
                "Risk is required",
                "error"
            );

            return false;
        }


        if (
            formData.plannedStartDate &&
            formData.plannedEndDate &&
            new Date(formData.plannedEndDate)
            <
            new Date(formData.plannedStartDate)
        ) {

            showAlert(
                "Planned end date cannot be before planned start date",
                "error"
            );

            return false;
        }


        return true;
    };


    /*
     * ============================================================
     * BUILD PAYLOAD
     * ============================================================
     */

    const buildPayload = () => {

        return {

            title:
                formData.title.trim(),

            description:
                formData.description?.trim() ||
                null,

            changeTypeId:
                formData.changeTypeId
                    ? Number(
                        formData.changeTypeId
                    )
                    : null,

            categoryId:
                formData.categoryId
                    ? Number(
                        formData.categoryId
                    )
                    : null,

            priorityId:
                formData.priorityId
                    ? Number(
                        formData.priorityId
                    )
                    : null,

            riskId:
                formData.riskId
                    ? Number(
                        formData.riskId
                    )
                    : null,

            affectedServiceId:
                formData.affectedServiceId
                    ? Number(
                        formData.affectedServiceId
                    )
                    : null,

            affectedAssetId:
                formData.affectedAssetId
                    ? Number(
                        formData.affectedAssetId
                    )
                    : null,

            configurationItemId:
                formData.configurationItemId
                    ? Number(
                        formData.configurationItemId
                    )
                    : null,

            departmentId:
                formData.departmentId
                    ? Number(
                        formData.departmentId
                    )
                    : null,

            businessImpact:
                formData.businessImpact?.trim() ||
                null,

            plannedStartDate:
                toUTC(formData.plannedStartDate) ||
                null,

            plannedEndDate:
                toUTC(formData.plannedEndDate) ||
                null,

            estimatedDurationValue:
                formData.estimatedDurationValue
                    ? Number(
                        formData.estimatedDurationValue
                    )
                    : null,

            estimatedDurationUnit:
                formData.estimatedDurationUnit,

            expectedDowntimeValue:
                formData.expectedDowntimeValue
                    ? Number(
                        formData.expectedDowntimeValue
                    )
                    : null,

            expectedDowntimeUnit:
                formData.expectedDowntimeUnit,

            maintenanceWindowId:
                formData.maintenanceWindowId
                    ? Number(
                        formData.maintenanceWindowId
                    )
                    : null,

            implementationPlan:
                formData.implementationPlan?.trim() ||
                null,

            testingPlan:
                formData.testingPlan?.trim() ||
                null,

            rollbackPlan:
                formData.rollbackPlan?.trim() ||
                null,

            approverIds:
                formData.approverIds || []
        };
    };


    /*
     * ============================================================
     * SAVE DRAFT
     * ============================================================
     */

    const handleSaveDraft = async () => {

        if (!formData.title?.trim()) {

            showAlert(
                "Change title is required",
                "error"
            );

            return;
        }

        setSaving(true);

        try {

            const payload =
                buildPayload();

            let response;

            if (changeId) {

                response =
                    await AxiosInstance.put(
                        `${CHANGE_REQUEST_URL}/${changeId}/draft`,
                        payload
                    );

            } else {

                response =
                    await AxiosInstance.post(
                        `${CHANGE_REQUEST_URL}/draft`,
                        payload
                    );

                const change =
                    response.data?.data ||
                    response.data;

                if (attachments.length > 0) {

                    await saveAttachments(
                        change.id
                    );
                }
            }


            showAlert(
                "Change draft saved successfully",
                "success"
            );


            onSaved?.(
                response.data
            );

            handleClose();

        } catch (error) {

            console.error(
                "Unable to save draft",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to save change draft",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    const saveAttachments = async (changeId) => {

        if (!attachments.length) {
            return;
        }

        const formData = new FormData();

        attachments.forEach(file => {

            formData.append(
                "files",
                file
            );

        });

        await AxiosInstance.post(
            `${CHANGE_REQUEST_URL}/${changeId}/attachments`,
            formData
        );
    };

    /*
     * ============================================================
     * SUBMIT CLICK
     * ============================================================
     */

    const handleSubmitClick = () => {

        if (!validate()) {
            return;
        }

        setConfirmOpen(true);
    };


    /*
     * ============================================================
     * CONFIRM SUBMIT
     * ============================================================
     */

    const handleConfirmSubmit = async () => {

        setSaving(true);

        try {

            const payload =
                buildPayload();

            let response;

            if (changeId) {

                response =
                    await AxiosInstance.put(
                        `${CHANGE_REQUEST_URL}/${changeId}/submit`,
                        payload
                    );

            } else {

                response =
                    await AxiosInstance.post(
                        `${CHANGE_REQUEST_URL}/submit`,
                        payload
                    );

                const change =
                    response.data?.data ||
                    response.data;

                if (attachments.length > 0) {

                    await saveAttachments(
                        change.id
                    );
                }
            }

            showAlert(
                "Change submitted successfully",
                "success"
            );


            setConfirmOpen(false);


            onSaved?.(
                response.data
            );

            handleClose();

        } catch (error) {

            console.error(
                "Unable to submit change",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to submit change",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    /*
     * ============================================================
     * ATTACHMENTS
     * ============================================================
     */

    const handleFileSelect = (
        event
    ) => {

        const files =
            Array.from(
                event.target.files || []
            );

        if (!files.length) {
            return;
        }

        setAttachments(prev => [
            ...prev,
            ...files
        ]);

        event.target.value = "";
    };


    const handleDrop = (
        event
    ) => {

        event.preventDefault();

        const files =
            Array.from(
                event.dataTransfer.files || []
            );

        if (!files.length) {
            return;
        }

        setAttachments(prev => [
            ...prev,
            ...files
        ]);
    };


    const removeAttachment = (
        index
    ) => {

        setAttachments(prev =>
            prev.filter(
                (_, i) =>
                    i !== index
            )
        );
    };


    /*
     * ============================================================
     * CLOSE
     * ============================================================
     */

    const handleClose = () => {

        if (saving) {
            return;
        }

        setConfirmOpen(false);

        setFormData(
            initialForm
        );

        setAttachments([]);

        setUsers([]);

        onClose?.();
    };


    /*
     * ============================================================
     * SELECTED OBJECTS
     * ============================================================
     */

    const selectedAsset =
        findOption(
            assets,
            formData.affectedAssetId
        );


    const selectedConfigurationItem =
        findOption(
            configurationItems,
            formData.configurationItemId
        );


    const selectedApprovers =
        users.filter(
            user =>
                (formData.approverIds || [])
                    .map(String)
                    .includes(
                        String(user.id)
                    )
        );


    /*
     * ============================================================
     * FORMAT DATE
     * ============================================================
     */

    function formatDateTimeLocal(
        value
    ) {

        if (!value) {
            return "";
        }

        const date =
            new Date(value);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "";
        }

        const pad =
            number =>
                String(number)
                    .padStart(2, "0");

        return `${date.getFullYear()}-${pad(
            date.getMonth() + 1
        )}-${pad(
            date.getDate()
        )}T${pad(
            date.getHours()
        )}:${pad(
            date.getMinutes()
        )}`;
    }


    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (
        <ThemeProvider>

            <Dialog
                open={open}
                onClose={
                    saving
                        ? undefined
                        : handleClose
                }
                fullWidth
                maxWidth="lg"
            >

                {/* =================================================
                    HEADER
                ================================================== */}

                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 3,
                        py: 2
                    }}
                >

                    <Box>

                        <Typography
                            variant="h6"
                            fontWeight={600}
                        >
                            {changeId
                                ? "Edit Change Request"
                                : "Create Change Request"}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Provide the details required to assess,
                            approve and implement this change.
                        </Typography>

                    </Box>

                    <IconButton
                        onClick={handleClose}
                        disabled={saving}
                    >
                        <CloseIcon />
                    </IconButton>

                </DialogTitle>


                {/* =================================================
                    CONTENT
                ================================================== */}

                <DialogContent
                    dividers
                    sx={{
                        p: 3,
                        backgroundColor:
                            "background.default"
                    }}
                >

                    {loadingMasters ||
                        loadingChange ? (

                        <Box
                            sx={{
                                minHeight: 450,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <CircularProgress />
                        </Box>

                    ) : (

                        <Stack spacing={3}>


                            {/* =================================================
                                1. CHANGE INFORMATION
                            ================================================== */}

                            <ChangeSection
                                number="1"
                                title="Change Information"
                                description="Provide the basic information and classification of the change."
                            >

                                <Grid
                                    container
                                    spacing={2}
                                    sx={{
                                        width: "100%"
                                    }}
                                >

                                    <Grid size={12}>

                                        <TextField
                                            fullWidth
                                            required
                                            label="Change Title"
                                            name="title"
                                            value={
                                                formData.title
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            placeholder="Enter change title"
                                        />

                                    </Grid>


                                    <Grid size={12}>

                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            maxRows={6}
                                            label="Description"
                                            name="description"
                                            value={
                                                formData.description
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            placeholder="Describe the purpose, scope and reason for this change"
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <LookupSelect
                                            label="Change Type"
                                            name="changeTypeId"
                                            value={
                                                formData.changeTypeId
                                            }
                                            options={
                                                changeTypes
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            required
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <LookupSelect
                                            label="Category"
                                            name="categoryId"
                                            value={
                                                formData.categoryId
                                            }
                                            options={
                                                categories
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            required
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <LookupSelect
                                            label="Priority"
                                            name="priorityId"
                                            value={
                                                formData.priorityId
                                            }
                                            options={
                                                priorities
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            required
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <LookupSelect
                                            label="Risk"
                                            name="riskId"
                                            value={
                                                formData.riskId
                                            }
                                            options={
                                                risks
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            required
                                        />

                                    </Grid>

                                </Grid>

                            </ChangeSection>


                            {/* =================================================
                                2. IMPACT INFORMATION
                            ================================================== */}

                            <ChangeSection
                                number="2"
                                title="Impact Information"
                                description="Identify services, assets, configuration items and business impact."
                            >

                                <Grid
                                    container
                                    spacing={2}
                                >

                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <LookupSelect
                                            label="Affected Service"
                                            name="affectedServiceId"
                                            value={
                                                formData.affectedServiceId
                                            }
                                            options={
                                                affectedServices
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <Autocomplete
                                            fullWidth
                                            options={
                                                Array.isArray(
                                                    assets
                                                )
                                                    ? assets
                                                    : []
                                            }
                                            value={
                                                selectedAsset
                                            }
                                            isOptionEqualToValue={
                                                (
                                                    option,
                                                    value
                                                ) =>
                                                    String(
                                                        option?.id
                                                    ) ===
                                                    String(
                                                        value?.id
                                                    )
                                            }
                                            getOptionLabel={
                                                option =>
                                                    option?.name ||
                                                    ""
                                            }
                                            onChange={
                                                (
                                                    _,
                                                    value
                                                ) =>
                                                    setFormData(
                                                        prev => ({
                                                            ...prev,
                                                            affectedAssetId:
                                                                value?.id
                                                                    ? String(
                                                                        value.id
                                                                    )
                                                                    : ""
                                                        })
                                                    )
                                            }
                                            renderInput={
                                                params => (
                                                    <TextField
                                                        {...params}
                                                        label="Affected Asset"
                                                        placeholder="Search asset"
                                                    />
                                                )
                                            }
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <Autocomplete
                                            fullWidth
                                            options={
                                                Array.isArray(
                                                    configurationItems
                                                )
                                                    ? configurationItems
                                                    : []
                                            }
                                            value={
                                                selectedConfigurationItem
                                            }
                                            isOptionEqualToValue={
                                                (
                                                    option,
                                                    value
                                                ) =>
                                                    String(
                                                        option?.id
                                                    ) ===
                                                    String(
                                                        value?.id
                                                    )
                                            }
                                            getOptionLabel={
                                                option =>
                                                    option?.name ||
                                                    ""
                                            }
                                            onChange={
                                                (
                                                    _,
                                                    value
                                                ) =>
                                                    setFormData(
                                                        prev => ({
                                                            ...prev,
                                                            configurationItemId:
                                                                value?.id
                                                                    ? String(
                                                                        value.id
                                                                    )
                                                                    : ""
                                                        })
                                                    )
                                            }
                                            renderInput={
                                                params => (
                                                    <TextField
                                                        {...params}
                                                        label="Configuration Item"
                                                        placeholder="Search configuration item"
                                                    />
                                                )
                                            }
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <LookupSelect
                                            label="Department"
                                            name="departmentId"
                                            value={
                                                formData.departmentId
                                            }
                                            options={
                                                departments
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </Grid>


                                    <Grid size={12}>

                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            maxRows={6}
                                            label="Business Impact"
                                            name="businessImpact"
                                            value={
                                                formData.businessImpact
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            placeholder="Describe the impact to users, business operations or services"
                                        />

                                    </Grid>

                                </Grid>

                            </ChangeSection>


                            {/* =================================================
                                3. SCHEDULING
                            ================================================== */}

                            <ChangeSection
                                number="3"
                                title="Scheduling"
                                description="Define the planned implementation period, duration and maintenance window."
                            >

                                <Grid
                                    container
                                    spacing={2}
                                >

                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <TextField
                                            fullWidth
                                            type="datetime-local"
                                            label="Planned Start"
                                            name="plannedStartDate"
                                            value={
                                                formData.plannedStartDate
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            slotProps={{
                                                inputLabel: {
                                                    shrink: true
                                                }
                                            }}
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <TextField
                                            fullWidth
                                            type="datetime-local"
                                            label="Planned End"
                                            name="plannedEndDate"
                                            value={
                                                formData.plannedEndDate
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            slotProps={{
                                                inputLabel: {
                                                    shrink: true
                                                }
                                            }}
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <DurationInput
                                            label="Estimated Duration"
                                            value={
                                                formData.estimatedDurationValue
                                            }
                                            unit={
                                                formData.estimatedDurationUnit
                                            }
                                            onValueChange={
                                                value =>
                                                    setFormData(
                                                        prev => ({
                                                            ...prev,
                                                            estimatedDurationValue:
                                                                value
                                                        })
                                                    )
                                            }
                                            onUnitChange={
                                                value =>
                                                    setFormData(
                                                        prev => ({
                                                            ...prev,
                                                            estimatedDurationUnit:
                                                                value
                                                        })
                                                    )
                                            }
                                        />

                                    </Grid>


                                    <Grid
                                        size={{
                                            xs: 12,
                                            md: 6
                                        }}
                                    >

                                        <DurationInput
                                            label="Expected Downtime"
                                            value={
                                                formData.expectedDowntimeValue
                                            }
                                            unit={
                                                formData.expectedDowntimeUnit
                                            }
                                            onValueChange={
                                                value =>
                                                    setFormData(
                                                        prev => ({
                                                            ...prev,
                                                            expectedDowntimeValue:
                                                                value
                                                        })
                                                    )
                                            }
                                            onUnitChange={
                                                value =>
                                                    setFormData(
                                                        prev => ({
                                                            ...prev,
                                                            expectedDowntimeUnit:
                                                                value
                                                        })
                                                    )
                                            }
                                        />

                                    </Grid>


                                    <Grid size={12}>

                                        <LookupSelect
                                            label="Maintenance Window"
                                            name="maintenanceWindowId"
                                            value={
                                                formData.maintenanceWindowId
                                            }
                                            options={
                                                maintenanceWindows
                                            }
                                            onChange={
                                                handleChange
                                            }
                                        />

                                    </Grid>

                                </Grid>

                            </ChangeSection>


                            {/* =================================================
                                4. IMPLEMENTATION INFORMATION
                            ================================================== */}

                            <ChangeSection
                                number="4"
                                title="Implementation Information"
                                description="Provide implementation, testing and rollback information and select approvers."
                            >

                                <Stack spacing={2}>

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                        label="Implementation Plan"
                                        name="implementationPlan"
                                        value={
                                            formData.implementationPlan
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        placeholder="Describe the implementation steps in detail"
                                    />


                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                        label="Testing Plan"
                                        name="testingPlan"
                                        value={
                                            formData.testingPlan
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        placeholder="Describe how the change will be tested"
                                    />


                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                        label="Rollback / Backout Plan"
                                        name="rollbackPlan"
                                        value={
                                            formData.rollbackPlan
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        placeholder="Describe how the change will be reversed if unsuccessful"
                                    />


                                    {/* Approvers */}

                                    <Autocomplete
                                        multiple
                                        fullWidth
                                        options={
                                            Array.isArray(users)
                                                ? users
                                                : []
                                        }
                                        value={
                                            selectedApprovers
                                        }
                                        isOptionEqualToValue={
                                            (
                                                option,
                                                value
                                            ) =>
                                                String(
                                                    option?.id
                                                ) ===
                                                String(
                                                    value?.id
                                                )
                                        }
                                        getOptionLabel={
                                            option =>
                                                option?.name ||
                                                option?.username ||
                                                ""
                                        }
                                        onChange={
                                            (
                                                _,
                                                values
                                            ) =>
                                                setFormData(
                                                    prev => ({
                                                        ...prev,
                                                        approverIds:
                                                            values.map(
                                                                item =>
                                                                    String(
                                                                        item.id
                                                                    )
                                                            )
                                                    })
                                                )
                                        }
                                        onInputChange={
                                            (
                                                _,
                                                value,
                                                reason
                                            ) => {

                                                if (
                                                    reason ===
                                                    "input"
                                                ) {
                                                    searchUsers(
                                                        value
                                                    );
                                                }
                                            }
                                        }
                                        renderOption={
                                            (
                                                props,
                                                option,
                                                { selected }
                                            ) => (
                                                <li
                                                    {...props}
                                                    key={
                                                        option.id
                                                    }
                                                >

                                                    <Checkbox
                                                        checked={
                                                            selected
                                                        }
                                                    />

                                                    <ListItemText
                                                        primary={
                                                            option.name
                                                        }
                                                        secondary={
                                                            option.email ||
                                                            option.username ||
                                                            ""
                                                        }
                                                    />

                                                </li>
                                            )
                                        }
                                        renderInput={
                                            params => (
                                                <TextField
                                                    {...params}
                                                    label="Approvers"
                                                    placeholder="Search and select approvers"
                                                />
                                            )
                                        }
                                    />


                                    {/* Attachments */}

                                    <AttachmentPanel
                                        attachments={
                                            attachments
                                        }
                                        onFilesSelected={
                                            handleFileSelect
                                        }
                                        onDrop={
                                            handleDrop
                                        }
                                        onRemove={
                                            removeAttachment
                                        }
                                    />

                                </Stack>

                            </ChangeSection>

                        </Stack>
                    )}

                </DialogContent>


                {/* =================================================
                    ACTIONS
                ================================================== */}

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: "1px solid",
                        borderColor: "divider"
                    }}
                >

                    <Button
                        onClick={
                            handleClose
                        }
                        disabled={
                            saving
                        }
                    >
                        Cancel
                    </Button>


                    <Box
                        sx={{
                            flex: 1
                        }}
                    />


                    <Button
                        variant="outlined"
                        onClick={
                            handleSaveDraft
                        }
                        disabled={
                            saving ||
                            loadingMasters
                        }
                    >
                        {saving
                            ? "Saving..."
                            : "Save Draft"}
                    </Button>


                    <Button
                        variant="contained"
                        onClick={
                            handleSubmitClick
                        }
                        disabled={
                            saving ||
                            loadingMasters
                        }
                        startIcon={
                            saving
                                ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                )
                                : null
                        }
                    >
                        Submit
                    </Button>

                </DialogActions>

            </Dialog>


            {/* =====================================================
                SUBMIT CONFIRMATION
            ====================================================== */}

            <Dialog
                open={confirmOpen}
                onClose={() => {

                    if (!saving) {
                        setConfirmOpen(false);
                    }

                }}
                maxWidth="sm"
                fullWidth
            >

                <DialogTitle
                    sx={{
                        fontWeight: 600
                    }}
                >
                    Submit Change {" for "}
                    {changeId || "New"}
                    {" "} Assessment
                </DialogTitle>


                <DialogContent
                    dividers
                >

                    <Stack spacing={2}>

                        <ConfirmationRow
                            label="Change Type"
                            value={
                                findOption(
                                    changeTypes,
                                    formData.changeTypeId
                                )?.name || "-"
                            }
                        />

                        <ConfirmationRow
                            label="Risk"
                            value={
                                findOption(
                                    risks,
                                    formData.riskId
                                )?.name || "-"
                            }
                        />

                        <ConfirmationRow
                            label="Affected Service"
                            value={
                                findOption(
                                    affectedServices,
                                    formData.affectedServiceId
                                )?.name || "-"
                            }
                        />

                        <ConfirmationRow
                            label="Planned Date"
                            value={
                                formData.plannedStartDate
                                    ? formatDisplayDate(
                                        formData.plannedStartDate
                                    )
                                    : "-"
                            }
                        />

                    </Stack>

                </DialogContent>


                <DialogActions
                    sx={{
                        px: 3,
                        py: 2
                    }}
                >

                    <Button
                        onClick={() =>
                            setConfirmOpen(false)
                        }
                        disabled={
                            saving
                        }
                    >
                        Cancel
                    </Button>


                    <Button
                        variant="contained"
                        onClick={
                            handleConfirmSubmit
                        }
                        disabled={
                            saving
                        }
                        startIcon={
                            saving
                                ? (
                                    <CircularProgress
                                        size={18}
                                        color="inherit"
                                    />
                                )
                                : null
                        }
                    >
                        {saving
                            ? "Submitting..."
                            : "Submit Change"}
                    </Button>

                </DialogActions>

            </Dialog>

        </ThemeProvider>
    );
};


/*
 * ================================================================
 * CHANGE SECTION
 * ================================================================
 */

const ChangeSection = ({
    number,
    title,
    description,
    children
}) => {

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden"
            }}
        >

            <Box
                sx={{
                    px: 2.5,
                    py: 2,
                    backgroundColor:
                        "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider"
                }}
            >

                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                >

                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor:
                                "primary.main",
                            color:
                                "primary.contrastText",
                            fontWeight: 600
                        }}
                    >
                        {number}
                    </Box>


                    <Box>

                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                        >
                            {title}
                        </Typography>

                        {description && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {description}
                            </Typography>
                        )}

                    </Box>

                </Stack>

            </Box>


            <Box
                sx={{
                    p: 2.5,
                    width: "100%",
                    boxSizing: "border-box"
                }}
            >
                {children}
            </Box>

        </Paper>
    );
};


/*
 * ================================================================
 * LOOKUP SELECT
 * ================================================================
 */

const LookupSelect = ({
    label,
    name,
    value,
    options = [],
    onChange,
    required = false
}) => {

    return (
        <FormControl
            fullWidth
            required={required}
        >

            <InputLabel>
                {label}
            </InputLabel>

            <Select
                name={name}
                value={
                    value ?? ""
                }
                label={label}
                onChange={
                    onChange
                }
            >

                <MenuItem value="">
                    <em>
                        Select {label}
                    </em>
                </MenuItem>


                {options.map(
                    item => (

                        <MenuItem
                            key={item.id}
                            value={item.id}
                        >
                            {item.name}
                        </MenuItem>

                    )
                )}

            </Select>

        </FormControl>
    );
};


/*
 * ================================================================
 * DURATION INPUT
 * ================================================================
 */

const DurationInput = ({
    label,
    value,
    unit,
    onValueChange,
    onUnitChange
}) => {

    return (
        <Grid
            container
            spacing={1}
        >

            <Grid
                size={{
                    xs: 7,
                    sm: 8
                }}
            >

                <TextField
                    fullWidth
                    type="number"
                    label={label}
                    value={
                        value ?? ""
                    }
                    onChange={
                        event =>
                            onValueChange(
                                event.target.value
                            )
                    }
                    slotProps={{
                        htmlInput: {
                            min: 0
                        }
                    }}
                />

            </Grid>


            <Grid
                size={{
                    xs: 5,
                    sm: 4
                }}
            >

                <FormControl
                    fullWidth
                >

                    <InputLabel>
                        Unit
                    </InputLabel>

                    <Select
                        value={
                            unit ||
                            "HOURS"
                        }
                        label="Unit"
                        onChange={
                            event =>
                                onUnitChange(
                                    event.target.value
                                )
                        }
                    >

                        <MenuItem value="MINUTES">
                            Minutes
                        </MenuItem>

                        <MenuItem value="HOURS">
                            Hours
                        </MenuItem>

                    </Select>

                </FormControl>

            </Grid>

        </Grid>
    );
};


/*
 * ================================================================
 * ATTACHMENT PANEL
 * ================================================================
 */

const AttachmentPanel = ({
    attachments = [],
    onFilesSelected,
    onDrop,
    onRemove
}) => {

    return (
        <Box>

            <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{
                    mb: 1
                }}
            >
                Attachments
            </Typography>


            <Paper
                variant="outlined"
                onDragOver={
                    event =>
                        event.preventDefault()
                }
                onDrop={
                    onDrop
                }
                sx={{
                    p: 3,
                    textAlign: "center",
                    borderStyle: "dashed",
                    cursor: "pointer",
                    backgroundColor:
                        "background.default"
                }}
            >

                <CloudUploadOutlinedIcon
                    sx={{
                        fontSize: 40,
                        color: "text.secondary",
                        mb: 1
                    }}
                />


                <Typography
                    variant="body2"
                    fontWeight={500}
                >
                    Drag & Drop files here
                </Typography>


                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        my: 1
                    }}
                >
                    or
                </Typography>


                <Button
                    variant="outlined"
                    component="label"
                    startIcon={
                        <AttachFileOutlinedIcon />
                    }
                >
                    Browse Files

                    <input
                        type="file"
                        hidden
                        multiple
                        onChange={
                            onFilesSelected
                        }
                    />

                </Button>

            </Paper>


            {attachments.length > 0 && (

                <Stack
                    spacing={1}
                    sx={{
                        mt: 2
                    }}
                >

                    {attachments.map(
                        (file, index) => (

                            <Paper
                                key={
                                    `${file.name}-${index}`
                                }
                                variant="outlined"
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between"
                                }}
                            >

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        minWidth: 0
                                    }}
                                >

                                    <AttachFileOutlinedIcon
                                        fontSize="small"
                                        sx={{
                                            mr: 1
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            minWidth: 0
                                        }}
                                    >

                                        <Typography
                                            variant="body2"
                                            noWrap
                                        >
                                            {file.name}
                                        </Typography>

                                        {file.size && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {formatFileSize(
                                                    file.size
                                                )}
                                            </Typography>
                                        )}

                                    </Box>

                                </Box>


                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                        onRemove(
                                            index
                                        )
                                    }
                                >
                                    <DeleteOutlineIcon
                                        fontSize="small"
                                    />
                                </IconButton>

                            </Paper>

                        )
                    )}

                </Stack>

            )}

        </Box>
    );
};


/*
 * ================================================================
 * CONFIRMATION ROW
 * ================================================================
 */

const ConfirmationRow = ({
    label,
    value
}) => {

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2
            }}
        >

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    width: 150,
                    minWidth: 150
                }}
            >
                {label}
            </Typography>


            <Typography
                variant="body2"
                fontWeight={500}
            >
                {value}
            </Typography>

        </Box>
    );
};


/*
 * ================================================================
 * HELPERS
 * ================================================================
 */

const formatFileSize = (
    bytes
) => {

    if (!bytes) {
        return "0 KB";
    }

    const kb =
        bytes / 1024;

    if (kb < 1024) {
        return `${kb.toFixed(1)} KB`;
    }

    return `${(
        kb / 1024
    ).toFixed(1)} MB`;
};


const formatDisplayDate = (
    value
) => {

    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return value;
    }

    return date.toLocaleString();
};


export default CreateChangeDialog;